<template>
    <ion-modal
        ref="feedbackModal"
        class="feedbackModal primaryHeader"
        :enter-animation="slideInFromRight"
        :leave-animation="slideOutToRight"
        @didDismiss="onModalDidDismiss"
    >
        <ion-page class="feedbackPage">
            <ion-header class="ion-no-border" :translucent="false">
                <ion-toolbar class="feedbackToolbar" lines="none">
                    <ion-buttons slot="start" lines="none">
                        <ion-icon :icon="feedbackIcon" class="feedbackTitleIcon"></ion-icon>
                    </ion-buttons>
                    <ion-title>{{ $t('feedback.title') }}</ion-title>
                    <ion-buttons slot="end" lines="none">
                        <ion-button fill="clear" @click="close" lines="none">
                            <ion-icon :icon="closeOutline" slot="icon-only"></ion-icon>
                        </ion-button>
                    </ion-buttons>
                </ion-toolbar>
            </ion-header>

            <ion-content class="feedbackContent">
                <div class="feedbackForm ion-padding">

                    <!-- Category -->
                    <div class="fieldBlock">
                        <label class="fieldLabel">{{ $t('feedback.category') }}</label>
                        <ion-select
                            v-model="form.category"
                            interface="action-sheet"
                            :cancel-text="$t('cancel')"
                            :placeholder="$t('feedback.selectCategory')"
                            class="fieldControl select"
                            aria-label="category"
                        >
                            <ion-select-option v-for="cat in categories" :key="cat.value" :value="cat.value">
                                {{ $t('feedback.categories.' + cat.label) }}
                            </ion-select-option>
                        </ion-select>
                    </div>

                    <!-- Urgent (only meaningful for bug reports, hidden for new ideas) -->
                    <div v-if="form.category === 2" class="fieldBlock urgentBlock">
                        <ion-item lines="none" class="checkboxContainer">
                            <ion-checkbox v-model="form.critical" slot="start"></ion-checkbox>
                            <ion-label class="checkboxLabel">{{ $t('feedback.urgent') }}</ion-label>
                        </ion-item>
                    </div>

                    <!-- Title -->
                    <div class="fieldBlock">
                        <label class="fieldLabel">{{ $t('feedback.feedbackTitle') }}</label>
                        <ion-input
                            v-model="form.title"
                            class="fieldControl"
                            :placeholder="$t('feedback.feedbackTitlePlaceholder')"
                            :class="{ 'has-error': !form.title.trim() }"
                            @ionInput="errors.title = false"
                        ></ion-input>
                        <div v-if="errors.title" class="fieldError">{{ $t('feedback.titleRequired') }}</div>
                    </div>

                    <!-- Description -->
                    <div class="fieldBlock">
                        <label class="fieldLabel">{{ $t('feedback.description') }}</label>
                        <ion-textarea
                            v-model="form.description"
                            class="fieldControl textarea"
                            :auto-grow="true"
                            :rows="5"
                            :placeholder="$t('feedback.descriptionPlaceholder')"
                            :class="{ 'has-error': !form.description.trim() }"
                            @ionInput="errors.description = false"
                        ></ion-textarea>
                        <div v-if="errors.description" class="fieldError">{{ $t('feedback.descriptionRequired') }}</div>
                    </div>

                    <!-- Attachments -->
                    <div class="fieldBlock">
                        <label class="fieldLabel">{{ $t('feedback.attachments') }}</label>
                        <ion-button fill="outline" class="attachButton" @click="triggerFileInput">
                            <ion-icon :icon="attachOutline" slot="start"></ion-icon>
                            {{ $t('feedback.addFiles') }}
                        </ion-button>
                        <input ref="fileInput" type="file" hidden multiple @change="onFilesPicked" />
                        <div v-if="attachments.length" class="attachmentsRow">
                            <div v-for="(f, i) in attachments" :key="f.__id" class="fileChip">
                                <ion-icon :icon="documentAttachOutline" class="fileChipIcon"></ion-icon>
                                <span class="fileChipName">{{ f.name }}</span>
                                <span class="fileChipSize">({{ formatBytes(f.size) }})</span>
                                <ion-button fill="clear" class="fileChipClose" @click="removeAttachment(i)">
                                    <ion-icon :icon="closeOutline" slot="icon-only"></ion-icon>
                                </ion-button>
                            </div>
                        </div>
                    </div>

                    <!-- Device info (read-only, what we send along) -->
                    <div class="fieldBlock deviceInfoBlock">
                        <div class="deviceInfoToggle" @click="showDeviceInfo = !showDeviceInfo">
                            <ion-icon :icon="phonePortraitOutline"></ion-icon>
                            <span>{{ $t('feedback.deviceInfo') }}</span>
                            <ion-icon :icon="showDeviceInfo ? chevronUpOutline : chevronDownOutline" class="chevron"></ion-icon>
                        </div>
                        <div v-if="showDeviceInfo" class="deviceInfoList">
                            <div class="deviceInfoRow"><span>{{ $t('feedback.model') }}</span><span>{{ device.model || '-' }}</span></div>
                            <div class="deviceInfoRow"><span>{{ $t('feedback.os') }}</span><span>{{ device.operatingSystem }} {{ device.osVersion }}</span></div>
                            <div class="deviceInfoRow"><span>{{ $t('feedback.screenSize') }}</span><span>{{ device.screenSize }}</span></div>
                            <div class="deviceInfoRow"><span>{{ $t('feedback.appVersion') }}</span><span>{{ device.appVersion }}</span></div>
                        </div>
                    </div>
                </div>
            </ion-content>

            <!-- Actions live in a fixed footer (outside the scroll area) so they
                 are always reachable regardless of form length / modal height. -->
            <ion-footer class="ion-no-border feedbackFooter">
                <div class="footerInner">
                    <ion-button expand="block" class="submitButton" :disabled="submitting" @click="submit(false)">
                        <ion-spinner v-if="submitting && submittingMode === false" name="circular" class="submitSpinner"></ion-spinner>
                        <template v-else>
                            <ion-icon :icon="sendOutline" slot="start"></ion-icon>
                            {{ $t('feedback.send') }}
                        </template>
                    </ion-button>
                    <ion-button expand="block" fill="outline" class="submitButton secondary" :disabled="submitting" @click="submit(true)">
                        <ion-spinner v-if="submitting && submittingMode === true" name="circular" class="submitSpinner secondary"></ion-spinner>
                        <template v-else>
                            <ion-icon :icon="cameraOutline" slot="start"></ion-icon>
                            {{ $t('feedback.sendWithScreenshot') }}
                        </template>
                    </ion-button>
                </div>
            </ion-footer>
        </ion-page>
    </ion-modal>
</template>

<script>
import {
    IonModal, IonPage, IonHeader, IonToolbar, IonTitle, IonContent, IonFooter,
    IonButtons, IonButton, IonIcon, IonSelect, IonSelectOption, IonInput,
    IonTextarea, IonCheckbox, IonSpinner, IonItem, IonLabel,
    alertController, toastController
} from '@ionic/vue';
import {
    closeOutline, sendOutline, cameraOutline, attachOutline, documentAttachOutline,
    chevronDownOutline, chevronUpOutline, phonePortraitOutline
} from 'ionicons/icons';
import feedbackIcon from '../../assets/icons/feedback.svg';
import { slideInFromRight, slideOutToRight } from '../../js/slideFromRight.js';
import { Capacitor } from '@capacitor/core';
import { Device } from '@capacitor/device';
import { Screenshot } from 'capacitor-screenshot';
import axios from 'axios';
import { getSystemVersion } from '@/js/functions';
import { config } from '@/js/config.js';

export default {
    components: {
        IonModal, IonPage, IonHeader, IonToolbar, IonTitle, IonContent, IonFooter,
        IonButtons, IonButton, IonIcon, IonSelect, IonSelectOption, IonInput,
        IonTextarea, IonCheckbox, IonSpinner, IonItem, IonLabel
    },
    props: ['openFeedback', 'page'],
    data() {
        return {
            categories: [
                { value: 2, label: 'bug' },
                { value: 3, label: 'newIdea' }
            ],
            form: {
                category: 2,
                critical: false,
                title: '',
                description: ''
            },
            errors: {
                title: false,
                description: false
            },
            attachments: [],
            showDeviceInfo: true,
            submitting: false,
            submittingMode: null,
            device: {
                model: '',
                operatingSystem: '',
                osVersion: '',
                platform: '',
                manufacturer: '',
                webViewVersion: '',
                screenSize: '',
                appVersion: '',
                deviceId: ''
            },
            systemVersion: getSystemVersion(),

            //ICONS
            closeOutline,
            sendOutline,
            cameraOutline,
            attachOutline,
            documentAttachOutline,
            feedbackIcon,
            chevronDownOutline,
            chevronUpOutline,
            phonePortraitOutline
        };
    },
    methods: {
        close() {
            this.$emit('show-feedback', 'close');
        },
        onModalDidDismiss() {
            this.$emit('show-feedback', 'close');
        },
        triggerFileInput() {
            this.$refs.fileInput?.click();
        },
        onFilesPicked(e) {
            const files = Array.from(e.target.files || []);
            files.forEach(f => (f.__id = `${f.name}-${f.size}-${f.lastModified}-${this.attachments.length}`));
            const existing = new Set(this.attachments.map(a => `${a.name}-${a.size}-${a.lastModified}`));
            const toAdd = files.filter(f => !existing.has(`${f.name}-${f.size}-${f.lastModified}`));
            this.attachments.push(...toAdd);
            e.target.value = '';
        },
        removeAttachment(index) {
            this.attachments.splice(index, 1);
        },
        fileToBase64(file) {
            return new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => resolve(reader.result);
                reader.onerror = reject;
                reader.readAsDataURL(file);
            });
        },
        formatBytes(bytes) {
            if (!bytes) return '0 B';
            const units = ['B', 'KB', 'MB', 'GB'];
            const i = Math.floor(Math.log(bytes) / Math.log(1024));
            return `${(bytes / Math.pow(1024, i)).toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
        },
        resetForm() {
            this.form = { category: 2, critical: false, title: '', description: '' };
            this.errors = { title: false, description: false };
            this.attachments = [];
        },
        async collectDeviceInfo() {
            try {
                const info = await Device.getInfo();
                let deviceId = '';
                try {
                    deviceId = (await Device.getId()).identifier || '';
                } catch (e) {
                    deviceId = '';
                }

                const w = window.screen.width;
                const h = window.screen.height;
                const dpr = window.devicePixelRatio || 1;

                this.device = {
                    model: info.model || '',
                    operatingSystem: info.operatingSystem || '',
                    osVersion: info.osVersion || '',
                    platform: info.platform || '',
                    manufacturer: info.manufacturer || '',
                    webViewVersion: info.webViewVersion || '',
                    screenSize: `${w} x ${h} px (@${dpr}x)`,
                    appVersion: `${config.app.mainVersion}.${config.app.subVersion} (${config.app.majorVersion})`,
                    deviceId
                };
            } catch (e) {
                console.log('[feedback] could not collect device info', e);
            }
        },
        // 4-byte UTF-8 characters (emoji and other astral-plane chars) get the
        // stored text truncated server-side from the first such character on,
        // so strip them before sending.
        stripAstralChars(text) {
            return (text || '').replace(/[\u{10000}-\u{10FFFF}]/gu, '');
        },
        // Captured on demand when the user asks to send with a screenshot.
        // It must never run in the background while the user is filling in the
        // form (html2canvas blocks the main thread noticeably).
        // On phones the shot is taken natively — html2canvas is unreliable in
        // WKWebView (cross-origin images, rendering quirks). The browser build
        // falls back to html2canvas.
        async captureScreenshot() {
            if (Capacitor.isNativePlatform()) {
                const native = await this.captureNativeScreenshot();
                if (native) {
                    return native;
                }
            }
            return this.captureWebScreenshot();
        },
        // The native plugin shoots the real screen — which would include this
        // panel — so the modal is hidden for the moment of the capture.
        async captureNativeScreenshot() {
            const modalEl = this.$refs.feedbackModal && this.$refs.feedbackModal.$el;
            try {
                if (modalEl) {
                    modalEl.style.visibility = 'hidden';
                }
                // Give the WebView time to render the hidden state before the
                // native layer is rasterized.
                await new Promise(resolve => setTimeout(resolve, 150));
                const { base64 } = await Screenshot.take();
                if (!base64) {
                    return null;
                }
                // The native shot is a full-resolution PNG; recompress so the
                // upload stays reasonably small.
                return await this.compressScreenshot(`data:image/png;base64,${base64}`);
            } catch (e) {
                console.log('[feedback] native screenshot failed', e);
                return null;
            } finally {
                if (modalEl) {
                    modalEl.style.visibility = '';
                }
            }
        },
        compressScreenshot(dataUrl) {
            return new Promise(resolve => {
                const img = new Image();
                img.onload = () => {
                    try {
                        const scale = Math.min(1, 1080 / img.width);
                        const canvas = document.createElement('canvas');
                        canvas.width = Math.round(img.width * scale);
                        canvas.height = Math.round(img.height * scale);
                        const ctx = canvas.getContext('2d');
                        ctx.fillStyle = '#ffffff';
                        ctx.fillRect(0, 0, canvas.width, canvas.height);
                        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
                        const out = canvas.toDataURL('image/jpeg', 0.85);
                        resolve(out && out.startsWith('data:image') ? out : dataUrl);
                    } catch (e) {
                        console.log('[feedback] screenshot compression failed', e);
                        resolve(dataUrl);
                    }
                };
                img.onerror = () => resolve(dataUrl);
                img.src = dataUrl;
            });
        },
        // Browser fallback: the modal lives outside #main-content, so it is
        // never in the shot.
        async captureWebScreenshot() {
            try {
                const html2canvas = (await import('html2canvas')).default;

                const candidates = Array.from(document.querySelectorAll('#main-content'));
                const target = candidates.find(el => {
                    const r = el.getBoundingClientRect();
                    return r.width > 0 && r.height > 0;
                }) || document.body;

                const attempts = [
                    {},
                    { ignoreElements: el => ['IMG', 'VIDEO', 'CANVAS'].includes(el.tagName) }
                ];
                for (const extra of attempts) {
                    try {
                        const canvas = await html2canvas(target, {
                            logging: false,
                            useCORS: true,
                            allowTaint: false,
                            scale: 1,
                            backgroundColor: '#ffffff',
                            ...extra
                        });
                        if (!canvas || !canvas.width || !canvas.height) {
                            continue;
                        }
                        const dataUrl = canvas.toDataURL('image/jpeg', 0.85);
                        if (dataUrl && dataUrl.startsWith('data:image')) {
                            return dataUrl;
                        }
                    } catch (attemptError) {
                        console.log('[feedback] screenshot attempt failed', attemptError);
                    }
                }
                return null;
            } catch (e) {
                console.log('[feedback] screenshot capture failed', e);
                return null;
            }
        },
        validate() {
            this.errors.title = this.form.title.trim() === '';
            this.errors.description = this.form.description.trim() === '';
            return !this.errors.title && !this.errors.description;
        },
        async presentToast(message, color = 'success') {
            const toast = await toastController.create({
                message,
                duration: 2200,
                color,
                position: 'bottom'
            });
            await toast.present();
        },
        async submit(withScreenshot = false) {
            if (this.submitting) {
                return;
            }
            if (!this.validate()) {
                return;
            }

            this.submitting = true;
            this.submittingMode = withScreenshot;

            let screenshotData = null;
            if (withScreenshot) {
                screenshotData = await this.captureScreenshot();
                if (!screenshotData) {
                    await this.presentToast(this.$t('feedback.screenshotUnavailable'), 'danger');
                    this.submitting = false;
                    this.submittingMode = null;
                    return;
                }
            }

            const selectedIdentity = JSON.parse(localStorage.getItem('selectedIdentity'));
            const loginDatas = JSON.parse(localStorage.getItem('loginDatas'));

            try {
                const formData = new FormData();
                formData.append('identityId', selectedIdentity ? selectedIdentity.id : '');
                formData.append('userId', loginDatas && loginDatas.userId ? loginDatas.userId : '');
                formData.append('userName', loginDatas && loginDatas.fullName ? loginDatas.fullName : '');
                formData.append('email', selectedIdentity && selectedIdentity.email ? selectedIdentity.email : '');
                formData.append('category', this.form.category);
                formData.append('critical', this.form.critical ? 1 : 0);
                formData.append('title', this.stripAstralChars(this.form.title).trim());
                formData.append('description', this.stripAstralChars(this.form.description).trim());
                formData.append('systemVersion', this.systemVersion);
                formData.append('mobileApp', true);
                formData.append('page', this.page || '');

                // Device information as a single JSON field
                formData.append('deviceInfo', JSON.stringify(this.device));

                if (screenshotData) {
                    formData.append('screenshots[]', screenshotData);
                }

                // Additional user-attached files as base64 strings in an
                // "attachments" array (same form as screenshots)
                const attachmentsBase64 = await Promise.all(this.attachments.map(f => this.fileToBase64(f)));
                attachmentsBase64.forEach(b64 => formData.append('attachments[]', b64));

                const response = await axios.post(this.APIURL('sendFeedback'), formData, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });

                if (response.data && response.data.status === 'error') {
                    await this.presentToast(response.data.message || this.$t('feedback.sendError'), 'danger');
                } else {
                    await this.presentToast(this.$t('feedback.sendSuccess'), 'success');
                    this.resetForm();
                    this.close();
                }
            } catch (e) {
                console.log('[feedback] submit error', e);
                const alert = await alertController.create({
                    header: this.$t('error'),
                    message: this.$t('feedback.sendError'),
                    buttons: ['OK']
                });
                await alert.present();
            } finally {
                this.submitting = false;
                this.submittingMode = null;
            }
        }
    },
    watch: {
        'form.category'(val) {
            if (val !== 2) {
                this.form.critical = false;
            }
        },
        openFeedback() {
            if (this.openFeedback) {
                this.collectDeviceInfo();
                this.$refs.feedbackModal.$el.present();
            } else {
                this.$refs.feedbackModal.$el.dismiss(null, 'cancel');
            }
        }
    },
    setup() {
        return {
            slideInFromRight,
            slideOutToRight
        };
    }
};
</script>

<style scoped>
/* Right-side full-height panel.
   IMPORTANT: the host must be `position: fixed` pinned to the viewport (inset:0).
   With `position: absolute` the host resolved against an ancestor taller than the
   viewport, and Ionic's default `align-items: center` then vertically centered the
   full-height wrapper inside it — shifting the panel down by ~40px so the footer's
   bottom (the "send with screenshot" button) was clipped below the screen. Pinning
   the host to the viewport makes host height == wrapper height, so there is no
   centering offset. The page is a flex column (height:100%) so ion-content flexes
   and scrolls while ion-footer stays pinned at the bottom. */
.feedbackModal {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 9999;
    --width: 90% !important;
    --height: 100% !important;
    --border-radius: 0;
    justify-content: flex-end !important;
    padding-top: 0;
    padding-bottom: 0;
}

.feedbackPage {
    display: flex;
    flex-direction: column;
    height: 100%;
    background: #ffffff;
}

/* Header colors/background come from the shared global `.primaryHeader` styles
   (HU = blue gradient, PL/.metro = yellow), which also keep the title text
   background transparent. The shared style rounds the toolbar top corners for
   card-like modals; this is a full-height side panel, so keep it square. */
.feedbackToolbar {
    border-radius: 0 !important;
}

.feedbackToolbar ion-title {
    font-weight: 600;
    font-size: 20px;
}

.feedbackTitleIcon {
    font-size: 24px;
    margin-inline-start: 8px;
    background: transparent !important;
}

.feedbackContent {
    --background: #ffffff;
}

.fieldBlock {
    margin-bottom: 20px;
}

.fieldLabel {
    display: block;
    font-size: 16px;
    color: #444;
    margin-bottom: 8px;
}

.fieldControl {
    --background: #ffffff;
    --padding-start: 14px;
    --padding-end: 14px;
    border: 1px solid #d9d9d9;
    border-radius: 8px;
    width: 100%;
}

.fieldControl.select {
    min-height: 52px;
}

.fieldControl.textarea {
    --padding-top: 10px;
    --padding-bottom: 10px;
}

.fieldControl.has-error {
    border-color: var(--ion-color-primary);
    box-shadow: 0 0 0 1px var(--ion-color-primary);
}

.fieldError {
    margin-top: 6px;
    font-size: 13px;
    font-weight: 600;
    color: var(--ion-color-primary);
}

.urgentBlock {
    margin-top: 4px;
}

.checkboxContainer {
    --background: transparent;
    --padding-start: 0;
    --inner-padding-end: 0;
    --min-height: 32px;
}

.checkboxContainer ion-checkbox {
    --size: 22px;
    margin-inline-end: 12px;
}

.checkboxLabel {
    font-size: 16px;
    color: #333;
    white-space: normal;
}

.attachButton {
    --border-color: #d9d9d9;
    --color: var(--ion-color-primary);
    --border-radius: 8px;
    text-transform: none;
    font-weight: 600;
}

.attachButton ion-icon {
    background: transparent !important;
}

.attachmentsRow {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-top: 12px;
}

.fileChip {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 6px 8px 6px 12px;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    background: #f6f7f9;
}

.fileChipIcon {
    font-size: 20px;
    color: var(--ion-color-primary);
    flex-shrink: 0;
    background: transparent !important;
}

.fileChipName {
    flex: 1;
    font-size: 14px;
    color: #333;
    word-break: break-word;
}

.fileChipSize {
    font-size: 12px;
    color: #999;
    flex-shrink: 0;
}

.fileChipClose {
    --padding-start: 4px;
    --padding-end: 4px;
    --color: #888;
    margin: 0;
    height: 28px;
    flex-shrink: 0;
}

.fileChipClose ion-icon {
    background: transparent !important;
}

.deviceInfoBlock {
    border-top: 1px solid #eee;
    padding-top: 12px;
}

.deviceInfoToggle {
    display: flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;
    color: #555;
    font-size: 15px;
}

.deviceInfoToggle .chevron {
    margin-left: auto;
}

.deviceInfoList {
    margin-top: 10px;
    background: #f6f7f9;
    border-radius: 8px;
    padding: 8px 12px;
}

.deviceInfoRow {
    display: flex;
    justify-content: space-between;
    gap: 12px;
    padding: 5px 0;
    font-size: 13px;
    color: #444;
    border-bottom: 1px solid #ececec;
}

.deviceInfoRow:last-child {
    border-bottom: none;
}

.deviceInfoRow span:first-child {
    color: #888;
    flex-shrink: 0;
}

.deviceInfoRow span:last-child {
    text-align: right;
    word-break: break-word;
}

.feedbackFooter {
    background: #ffffff;
}

.footerInner {
    background: #ffffff;
    padding: 12px 16px calc(12px + env(safe-area-inset-bottom));
    border-top: 1px solid #eee;
}

.submitButton {
    --background: var(--ion-color-primary);
    --background-activated: var(--ion-color-primary-shade);
    --background-focused: var(--ion-color-primary-shade);
    --background-hover: var(--ion-color-primary);
    --color: var(--ion-color-primary-contrast);
    --border-radius: 8px;
    font-weight: 700;
    height: 50px;
    text-transform: none;
    margin-bottom: 10px;
}

/* Icons are monochrome glyphs; make sure nothing paints a box behind them. */
.submitButton ion-icon {
    background: transparent !important;
    color: inherit;
}

.submitButton.secondary {
    --background: transparent;
    --background-activated: transparent;
    --background-focused: transparent;
    --background-hover: transparent;
    --color: var(--ion-color-primary);
    --border-color: var(--ion-color-primary);
    --border-width: 1px;
    margin-bottom: 0;
}

.submitSpinner {
    --color: var(--ion-color-primary-contrast);
}

.submitSpinner.secondary {
    --color: var(--ion-color-primary);
}
</style>
